'use client';

import * as React from 'react';
import type { IPaginationResponse } from '@/api/resort/types';
import { Diagnostics } from '@/util/diagnostics';

// Paging for a view that reads as one flat list but is served as several
// per-status lists. The region webcams grid is the case: six OpenFlag buckets
// fetched separately, then flattened in priority order so open cams surface
// first (see STATUS_ORDER in page-components/region/cams).
//
// Load More fills the earliest incomplete bucket, which is what keeps the flat
// order stable — buckets complete in priority order, so new rows land at the
// end of their own group rather than scattering through the grid.
//
// For a single list, use useLoadMoreResorts instead.

export type BucketPage<T> = {
  data?: T[];
  pagination?: IPaginationResponse;
};

type Args<K extends string | number, T> = {
  /** Bucket keys in the order their rows are rendered. */
  order: readonly K[];
  /** Server-rendered first page per bucket. */
  initial: Partial<Record<K, BucketPage<T> | undefined>>;
  fetchPage: (bucket: K, page: number) => Promise<BucketPage<T>>;
  pageSize: number;
  /** Identifies the view in diagnostics, e.g. `region 476 cams`. */
  label: string;
};

export type LoadMoreBuckets<T> = {
  /** Every loaded row, flattened in `order`. */
  rows: T[];
  /** Rows across the whole set, not the loaded slice. */
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  failed: boolean;
  loadMore: () => void;
};

export const useLoadMoreBuckets = <K extends string | number, T>({
  order, initial, fetchPage, pageSize, label,
}: Args<K, T>): LoadMoreBuckets<T> => {
  const seed = React.useMemo(() => {
    const rows = {} as Record<K, T[]>;
    const counts = {} as Record<K, number>;
    order.forEach((key) => {
      const bucket = initial[key];
      rows[key] = bucket?.data ?? [];
      counts[key] = bucket?.pagination?.count ?? rows[key].length;
    });
    return { rows, counts };
  }, [initial, order]);

  const [loaded, setLoaded] = React.useState<Record<K, T[]>>(seed.rows);
  const [isLoading, setLoading] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  const token = React.useRef(0);
  const loadedRef = React.useRef(loaded);
  loadedRef.current = loaded;

  // Keyed on the payload's shape rather than object identity, which changes
  // on every render and would reset paging mid-interaction.
  const seedKey = `${label}|${order.map((k) => `${String(k)}:${seed.counts[k]}:${seed.rows[k].length}`).join(',')}`;
  React.useEffect(() => {
    token.current += 1;
    setLoaded(seed.rows);
    setLoading(false);
    setFailed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey]);

  const rows = React.useMemo(
    () => order.flatMap((key) => loaded[key] ?? []),
    [order, loaded],
  );
  const total = React.useMemo(
    () => order.reduce((sum, key) => sum + seed.counts[key], 0),
    [order, seed],
  );

  const loadMore = React.useCallback(() => {
    if (isLoading) return;

    // The earliest bucket that still owes rows. Filling in order is what keeps
    // already-rendered rows from shuffling as new ones arrive.
    const next = order.find((key) => (loadedRef.current[key]?.length ?? 0) < seed.counts[key]);
    if (next === undefined) return;

    const mine = token.current + 1;
    token.current = mine;

    const page = Math.floor((loadedRef.current[next]?.length ?? 0) / pageSize);
    setLoading(true);
    setFailed(false);

    fetchPage(next, page)
      .then((response) => {
        if (token.current !== mine) return;
        const batch = response?.data ?? [];
        if (batch.length > 0) {
          setLoaded((prev) => ({ ...prev, [next]: [...(prev[next] ?? []), ...batch] }));
        }
      })
      .catch((error: Error) => {
        if (token.current !== mine) return;
        Diagnostics.error({
          tag: 'region-load-more',
          message: `${label}: failed to load ${String(next)} page ${page}`,
          error,
          data: { label, bucket: String(next), page },
        });
        setFailed(true);
      })
      .finally(() => {
        if (token.current === mine) setLoading(false);
      });
  }, [fetchPage, isLoading, label, order, pageSize, seed]);

  return {
    rows, total, hasMore: rows.length < total, isLoading, failed, loadMore,
  };
};
